import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge, Input } from "@learn-me/ui";
import { useCourses } from "@/features/courses/hooks/useCourses";
import { categoriesApi } from "@/features/categories/api/categoriesApi";
import {
  formatCurrency,
  formatDuration,
  COURSE_LEVEL_LABELS,
} from "@learn-me/utils";
import {
  Search,
  BookOpen,
  Filter,
  X,
  ArrowUpDown,
  Clock,
  Users,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeIn } from "@/components/animated";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useReducedMotion } from "@/hooks";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function CourseCatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const prefersReducedMotion = useReducedMotion();

  const filters = {
    page: Number(searchParams.get("page")) || 1,
    limit: 12,
    search: searchParams.get("search") || undefined,
    category: searchParams.get("category") || undefined,
    level: searchParams.get("level") || undefined,
    isFree: searchParams.get("isFree") === "true" ? true : undefined,
    sort:
      (searchParams.get("sort") as
        | "recent"
        | "popular"
        | "price-asc"
        | "price-desc"
        | "title") || undefined,
  };

  const { data, isLoading } = useCourses(filters);

  // Fetch main training areas for category filters
  const { data: mainAreas } = useQuery({
    queryKey: ["main-areas"],
    queryFn: categoriesApi.getMainAreas,
  });

  const selectedCategory = searchParams.get("category") || null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchInput) {
      newParams.set("search", searchInput);
    } else {
      newParams.delete("search");
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  // Get selected category name for breadcrumb
  const selectedCategoryName = mainAreas?.find(
    (a) => a.slug === selectedCategory,
  )?.name;

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: "Cursos", href: "/courses" },
    ...(selectedCategoryName ? [{ label: selectedCategoryName }] : []),
  ];

  return (
    <div className="py-8">
      <div className="container">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} className="mb-4" />

        {/* Header */}
        <FadeIn direction="up">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Aprenda a su ritmo</h1>
            <p className="text-muted-foreground max-w-3xl">
              Explore nuestros cursos digitales y acceda a experiencias de
              aprendizaje diseñadas para fortalecer conocimientos y competencias
              profesionales. Esta sección corresponde a la oferta autogestionada
              disponible dentro de la plataforma.
            </p>
          </div>
        </FadeIn>

        {/* Category Filter Pills */}
        {mainAreas && mainAreas.length > 0 && (
          <FadeIn direction="up" delay={0.1}>
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => handleFilterChange("category", null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  !selectedCategory
                    ? "bg-primary-600 text-white"
                    : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                }`}
              >
                Todos
                {mainAreas &&
                  mainAreas.reduce((sum, a) => sum + a.coursesCount, 0) > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${
                        !selectedCategory ? "bg-white/20" : "bg-secondary-200"
                      }`}
                    >
                      {mainAreas.reduce((sum, a) => sum + a.coursesCount, 0)}
                    </span>
                  )}
              </button>
              {mainAreas.map((area) => (
                <button
                  key={area.id}
                  onClick={() =>
                    handleFilterChange(
                      "category",
                      selectedCategory === area.slug ? null : area.slug,
                    )
                  }
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                    selectedCategory === area.slug
                      ? "bg-primary-600 text-white"
                      : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                  }`}
                  title={area.description || undefined}
                >
                  {area.name}
                  {area.coursesCount > 0 && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${
                        selectedCategory === area.slug
                          ? "bg-white/20"
                          : "bg-secondary-200"
                      }`}
                    >
                      {area.coursesCount}
                    </span>
                  )}
                  {selectedCategory === area.slug && (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              ))}
            </div>
          </FadeIn>
        )}

        {/* Filters */}
        <FadeIn direction="up" delay={0.15}>
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar cursos..."
                  className="pl-10"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit">Buscar</Button>
            </form>

            <div className="flex gap-2 flex-wrap">
              <select
                className="px-3 py-2 border rounded-lg bg-background"
                value={searchParams.get("level") || ""}
                onChange={(e) =>
                  handleFilterChange("level", e.target.value || null)
                }
              >
                <option value="">Todos los niveles</option>
                <option value="BEGINNER">Principiante</option>
                <option value="INTERMEDIATE">Intermedio</option>
                <option value="ADVANCED">Avanzado</option>
              </select>

              <select
                className="px-3 py-2 border rounded-lg bg-background"
                value={searchParams.get("sort") || "recent"}
                onChange={(e) =>
                  handleFilterChange("sort", e.target.value || null)
                }
              >
                <option value="recent">Más recientes</option>
                <option value="popular">Más populares</option>
                <option value="price-asc">Precio: menor a mayor</option>
                <option value="price-desc">Precio: mayor a menor</option>
                <option value="title">Alfabético</option>
              </select>

              <Button
                variant={
                  searchParams.get("isFree") === "true" ? "default" : "outline"
                }
                onClick={() =>
                  handleFilterChange(
                    "isFree",
                    searchParams.get("isFree") === "true" ? null : "true",
                  )
                }
              >
                Solo Gratis
              </Button>
            </div>
          </div>
        </FadeIn>

        {/* Results */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-muted" />
                <CardContent className="p-4">
                  <div className="h-6 bg-muted rounded mb-2" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data?.items.length === 0 ? (
          <FadeIn>
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                No se encontraron cursos
              </h2>
              <p className="text-muted-foreground mb-4">
                Intenta con otros terminos de busqueda o filtros.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchInput("");
                  setSearchParams({});
                }}
              >
                Limpiar filtros
              </Button>
            </div>
          </FadeIn>
        ) : (
          <>
            <motion.div
              layout
              className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {data?.items.map((course, index) => (
                  <motion.div
                    key={course.id}
                    layout
                    initial={
                      prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }
                    }
                    animate={{ opacity: 1, scale: 1 }}
                    exit={
                      prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }
                    }
                    transition={{
                      duration: 0.3,
                      delay: prefersReducedMotion ? 0 : index * 0.05,
                    }}
                  >
                    <Link to={`/courses/${course.slug}`}>
                      <motion.div
                        whileHover={prefersReducedMotion ? {} : { y: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                          <div className="aspect-video relative bg-muted">
                            {course.thumbnailUrl ? (
                              <img
                                src={
                                  normalizeImageUrl(course.thumbnailUrl) ?? ""
                                }
                                alt={course.title}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <BookOpen className="h-12 w-12 text-muted-foreground" />
                              </div>
                            )}
                            {/* Badges container */}
                            <div className="absolute top-2 right-2 flex flex-col gap-1">
                              {course.isFree && (
                                <Badge variant="success">Gratis</Badge>
                              )}
                              {!course.isFree &&
                                course.hasDiscount &&
                                course.listPrice != null &&
                                course.salePrice != null &&
                                Number(course.listPrice) >
                                  Number(course.salePrice) && (
                                  <Badge
                                    variant="default"
                                    className="bg-red-500"
                                  >
                                    {Math.round(
                                      ((Number(course.listPrice) -
                                        Number(course.salePrice)) /
                                        Number(course.listPrice)) *
                                        100,
                                    )}
                                    % OFF
                                  </Badge>
                                )}
                              {(course as any).isEnrolled && (
                                <Badge
                                  variant="default"
                                  className="bg-blue-500"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Inscrito
                                </Badge>
                              )}
                              {/* New badge for courses created in last 30 days */}
                              {course.createdAt &&
                                new Date(course.createdAt) >
                                  new Date(
                                    Date.now() - 30 * 24 * 60 * 60 * 1000,
                                  ) && (
                                  <Badge
                                    variant="default"
                                    className="bg-purple-500"
                                  >
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    Nuevo
                                  </Badge>
                                )}
                            </div>
                          </div>
                          <CardContent className="p-4 flex-1 flex flex-col">
                            <h3 className="font-semibold mb-2 line-clamp-2">
                              {course.title}
                            </h3>

                            {/* Course stats */}
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
                              {(course as any).chaptersCount !== undefined && (
                                <span className="flex items-center gap-1">
                                  <BookOpen className="h-3 w-3" />
                                  {(course as any).chaptersCount} lecciones
                                </span>
                              )}
                              {(course as any).duration &&
                                (course as any).duration > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDuration((course as any).duration)}
                                  </span>
                                )}
                              {(course as any).studentsCount !== undefined &&
                                (course as any).studentsCount > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {(course as any).studentsCount} estudiantes
                                  </span>
                                )}
                            </div>

                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
                              {course.description}
                            </p>

                            <div className="flex items-center justify-between mt-auto">
                              <span className="flex items-baseline gap-2">
                                {!course.isFree &&
                                  course.hasDiscount &&
                                  course.listPrice != null &&
                                  Number(course.listPrice) >
                                    Number(course.salePrice ?? 0) && (
                                    <span className="text-xs text-muted-foreground line-through">
                                      {formatCurrency(
                                        Number(course.listPrice),
                                        course.currency,
                                      )}
                                    </span>
                                  )}
                                <span className="font-bold">
                                  {course.isFree
                                    ? "Gratis"
                                    : formatCurrency(
                                        course.salePrice ?? 0,
                                        course.currency,
                                      )}
                                </span>
                              </span>
                              {course.level && (
                                <Badge variant="outline" className="text-xs">
                                  {COURSE_LEVEL_LABELS[course.level] ??
                                    course.level}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Pagination */}
            {data && data.pagination.totalPages > 1 && (
              <FadeIn delay={0.3}>
                <div className="flex justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    disabled={data.pagination.page === 1}
                    onClick={() => handlePageChange(data.pagination.page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center px-4">
                    Pagina {data.pagination.page} de{" "}
                    {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={
                      data.pagination.page === data.pagination.totalPages
                    }
                    onClick={() => handlePageChange(data.pagination.page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </FadeIn>
            )}
          </>
        )}
      </div>
    </div>
  );
}
